// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function AiHomeworkSolverTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Ai Homework Solver"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Ai
        Homework Solver” (the “App”). By downloading, installing, accessing, or
        using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        Ai Homework Solver is an educational support app. It allows you to take
        a photo of a homework problem or import an image from your gallery, uses
        optical character recognition (“OCR”) to extract text, and then sends
        the text to an AI model to generate step-by-step explanations and
        example solutions. The App may also let you view recent problems,
        access a history of solved tasks, save or export explanations (for
        example as PDF or text), and share results. Certain features, such as
        extended history, higher daily limits, or more detailed explanations,
        may require a paid subscription.
      </p>

      <div className={s.title}>
        3) Educational-Only Use and Academic Integrity (Important)
      </div>
      <p>
        The App is intended to help you understand concepts and learn how to
        solve problems by providing explanations, not to replace your own work.
        You must use the App in compliance with the rules of your school,
        university, test provider, or other institution. Many institutions
        restrict or prohibit turning in AI-generated answers as if they were
        your own work, or using such tools during exams or graded assignments.
      </p>
      <p>
        You agree not to use the App to cheat, to bypass academic integrity
        policies, or to obtain unfair advantage on exams, quizzes, or graded
        homework. You are solely responsible for how you use any explanations
        or outputs and for any consequences under your institution&apos;s rules.
        The App is an educational assistant, not a replacement for studying,
        attending classes, or completing your own assignments.
      </p>

      <div className={s.title}>4) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction, or with the
        consent and supervision of a parent or guardian where required. If you
        are a student, it is your responsibility to ensure that using the App
        is allowed under your school or institution&apos;s policies. Parents or
        guardians who allow minors to use the App remain responsible for that
        use.
      </p>

      <div className={s.title}>5) Accounts and Access</div>
      <p>
        The App may use Firebase Authentication or similar services to let you
        register and sign in with email, Apple, Google, or other supported
        methods. You are responsible for maintaining the confidentiality of your
        login credentials and for all activity that occurs under your account.
        You must promptly notify us of any suspected unauthorized use.
      </p>
      <p>
        In some modes, the App may be used without registration, in which case
        certain features (for example sync across devices or extended history)
        may be limited or unavailable. Without an account, data may be stored
        only locally on your device and can be lost if you uninstall the App,
        reset your device, or clear local storage.
      </p>

      <div className={s.title}>6) Camera, Photos, and OCR</div>
      <p>
        The App uses your device camera and/or photo library to capture images
        of homework questions or other text. OCR technology is then used to
        detect and extract text from the images. OCR can be imperfect and may
        misread characters, formulas, or diagrams. You should review and, where
        needed, correct or edit the recognized text before requesting a
        solution.
      </p>
      <p>
        You are responsible for ensuring that you have the right to photograph
        and upload any material you submit (for example, that you are not
        violating exam rules, copyright, or privacy rights). You must not upload
        images containing sensitive personal data about yourself or others
        unless strictly necessary and consistent with the App&apos;s educational
        purpose.
      </p>

      <div className={s.title}>7) AI-Generated Solutions and Limitations</div>
      <p>
        The App uses AI models (for example, GPT-style models accessed via a
        secure API) to generate step-by-step explanations and example solutions
        based on recognized text. AI outputs may contain inaccuracies, outdated
        information, or incomplete reasoning and may not match the methods or
        grading standards expected by your teacher or institution.
      </p>
      <p>
        You must treat AI outputs as study aids, not as guaranteed-correct
        answers. You are responsible for checking the logic, formulas, and final
        results before relying on them. The App does not guarantee that any
        solution is correct, applicable to your specific context, or sufficient
        to obtain a particular grade.
      </p>

      <div className={s.title}>8) Free Tier, Limits, and Fair Use</div>
      <p>
        The App may offer a free tier with limited access and a paid tier with
        extended features. In the free tier, you may have, for example:
      </p>
      <ul>
        <li>up to a limited number of solved questions per day (e.g. 5);</li>
        <li>access only to the last few results in history (e.g. 2 items);</li>
        <li>simplified or shorter explanations in some cases.</li>
      </ul>
      <p>
        We may implement technical limits on request frequency, content size, or
        usage patterns to protect the service and underlying AI APIs. You agree
        not to attempt to bypass these controls (for example by using automation
        or multiple accounts) or to use the App in a way that overloads or
        abuses the service.
      </p>

      <div className={s.title}>9) Subscriptions, Trials, and Billing</div>
      <p>
        Premium access may be offered through auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”), such as weekly and annual
        plans with a free trial (for example, a 3-day trial). Exact pricing,
        currencies, trial length, and package details are shown within the App
        and in the app stores and may vary by region.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and billing disputes are governed by the
        relevant app store policies.
      </p>

      <div className={s.title}>10) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features, such as unlimited daily solutions, extended history, or more
        detailed explanations. An internet connection may be required to
        validate or refresh entitlements after long offline periods. If a
        transaction is reversed, refunded, or flagged as fraudulent, related
        entitlements may be suspended or revoked.
      </p>

      <div className={s.title}>11) History, Storage, and Exports</div>
      <p>
        The App may store a history of solved tasks, including thumbnails of
        original images, recognized text, and AI-generated explanations. In the
        free tier, history may be limited to a small number of recent items;
        premium users may have extended or unlimited history. History and other
        data may be stored locally (for example in SQLite or Hive) and/or in
        cloud storage (for example via Firebase) to support backup and sync.
      </p>
      <p>
        Export features (for example saving a PDF or text file, or sharing via
        other apps) create copies of your content outside the App. Once exported
        or shared, those copies may be stored by other apps or services, which
        are outside our control. You are responsible for deciding where to store
        and whom to share your materials with.
      </p>

      <div className={s.title}>12) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, educational purposes and not
        to:
      </p>
      <ul>
        <li>
          violate academic integrity policies by submitting AI-generated
          solutions as your own work where this is prohibited;
        </li>
        <li>
          use the App during proctored exams, tests, or other assessments where
          outside help is not allowed;
        </li>
        <li>
          upload or request solutions for content that is illegal, hateful,
          threatening, or violates others&apos; privacy or intellectual property
          rights;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls;
        </li>
        <li>
          use automated scripts, bots, or scraping tools to interact with the
          App or its APIs without our prior permission.
        </li>
      </ul>

      <div className={s.title}>13) Intellectual Property</div>
      <p>
        You retain ownership of the original content you provide (such as your
        own photos and any additional text you type), subject to the rights
        required for the App and AI providers to process that content in order
        to generate solutions. AI-generated outputs may be subject to the terms
        of the underlying AI providers; subject to those terms, you generally
        may use explanations for personal study and learning.
      </p>
      <p>
        The App&apos;s software, design, layout, branding, and any content
        supplied by us (excluding your content and third-party materials) are
        owned by the publisher or its licensors and protected by intellectual
        property laws. Subject to these Terms and any Subscription limitations,
        you are granted a limited, non-exclusive, non-transferable, revocable
        license to use the App for personal educational purposes. You may not
        redistribute the App or its components as a standalone service or build
        a competing product using its assets without permission.
      </p>

      <div className={s.title}>14) Third-Party Services and AI Providers</div>
      <p>
        The App may rely on third-party services including but not limited to:
        Firebase (Auth, Firestore, Storage, Analytics, Crashlytics, Messaging),
        OCR providers (such as Google ML Kit or Cloud Vision), AI providers
        (such as OpenAI models accessed via a backend), and subscription
        management services (such as RevenueCat). These providers process data
        only to the extent necessary to operate features such as authentication,
        storage, OCR, AI responses, analytics, notifications, and billing.
      </p>
      <p>
        Each provider operates under its own terms and privacy policies. While
        we require them by contract to implement appropriate safeguards, we do
        not control their independent services and are not responsible for
        outages, rate limits, or other issues caused solely by those providers.
      </p>

      <div className={s.title}>15) Analytics and Notifications</div>
      <p>
        We may collect anonymized or pseudonymized analytics data (for example,
        via Firebase Analytics) to understand how the App is used, improve
        features, and detect technical issues. Typical events may include
        onboarding completion, problem solved, paywall viewed, trial started,
        and purchase success. Where feasible, analytics are aggregated and do
        not include the full text of your homework problems.
      </p>
      <p>
        With your permission, the App may send push notifications (for example,
        reminders to practice, updates about features, or status of long-running
        operations). You can manage notification permissions in your device
        settings and, where available, within the App. Disabling notifications
        may limit reminders but does not disable core solving functionality.
      </p>

      <div className={s.title}>16) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          any AI-generated solution will be correct, complete, or aligned with
          your teacher&apos;s expectations;
        </li>
        <li>
          OCR will correctly recognize all text, formulas, or diagrams;
        </li>
        <li>
          the App will be available at all times without interruption or error;
        </li>
        <li>
          using the App will result in better grades or academic outcomes.
        </li>
      </ul>

      <div className={s.title}>17) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        grades or opportunities, loss of data (including lost history or
        exports), or business interruption arising from or related to your use
        of, or inability to use, the App, including reliance on AI-generated
        solutions. Our total aggregate liability to you for direct damages
        arising out of or relating to the App will be limited to the amount you
        paid for Subscriptions during the twelve (12) months prior to the claim,
        or the minimum amount permitted by applicable law if greater. Some
        jurisdictions do not allow certain limitations or exclusions, so parts
        of this section may not apply to you.
      </p>

      <div className={s.title}>18) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your breach of these Terms, your
        violation of academic integrity rules, or your infringement of any
        third-party rights.
      </p>

      <div className={s.title}>19) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Ai Homework Solver, which explains how information is collected, used,
        stored, and shared, including photos you upload, recognized text,
        AI-generated content, device data, and analytics. In case of conflict
        between these Terms and the Privacy Policy regarding data processing,
        the Privacy Policy will govern how personal data is handled, while these
        Terms govern your use of the App and its features. You should review the
        Privacy Policy regularly to understand current practices.
      </p>

      <div className={s.title}>20) Changes, Governing Law, and Contact</div>
      <p>
        We may update the App, change feature sets (including free and premium
        features), or modify these Terms from time to time. The “Last updated”
        date at the top of this document indicates when these Terms were last
        revised. Material changes may be highlighted in the App or communicated
        through other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>
      <p>
        Applicable law and dispute resolution mechanisms may depend on your
        country of residence and mandatory consumer protection rules. Where
        permitted, you agree to first contact us to attempt to resolve any
        dispute informally. Platform-specific dispute or refund procedures
        (Apple, Google) may also apply to billing matters. Nothing in these
        Terms limits any non-waivable rights you may have under local law.
      </p>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account- or
        purchase-specific matters.
      </p>
    </TermOfUseLayout>
  );
}
